import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';

import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import { useLeadConfigService } from 'hooks/api/agents/config/lead';
import styles from './config.module.css';
import {
  SENIORITY_OPTIONS,
  TITLE_OPTIONS,
  LOCATION_OPTIONS,
  INDUSTRY_OPTIONS,
  DEPARTMENT_OPTIONS
} from './apollo-options';

const DEFAULT_CONFIG = {
  slug: 'default',

  // Apollo org search
  orgs_per_run: 20,
  org_locations: ['Nigeria'],
  org_industries: [],
  org_employee_min: 5,
  org_employee_max: 200,
  org_current_page: 1,
  org_depleted_through: 0,
  max_pages_per_run: 25,

  // Pipeline limits
  enrich_limit: 20,
  qualification_threshold: 40,
  max_outreach_per_run: 20,

  // Qualification rubric (max 100)
  score_audience: 30,
  score_key_contacts: 30,
  score_app: 25,
  score_newsletter: 15,
  social_followers_min: 1000,
  users_min: 10000,

  // Warming
  warming_event_code: null,
  warming_days: 14,

  // Meetings
  meeting_event_code: 'new_meeting',
  meeting_reschedule_event_code: 'meeting_rescheduled',
  meeting_held_event_code: 'meeting_held',
  meeting_no_show_event_code: 'meeting_no_show',
  meeting_cancel_event_code: 'meeting_canceled',
  meeting_reminder_hours: 24,
  meeting_reinvite_delay_minutes: 5,

  // Outreach
  automation_event_code: '',

  // Contact targeting
  contacts_per_org: 5,
  contact_titles: [],
  contact_departments: ['marketing', 'sales', 'growth'],
  contact_seniority: ['c_suite', 'vp', 'director', 'manager', 'owner']
};

/** Convert a string array to react-select option array */
const toOptions = (arr = []) => arr.map((v) => ({ label: v, value: v }));

/** Convert react-select option array back to string array */
const fromOptions = (opts = []) => (Array.isArray(opts) ? opts.map((o) => o.value) : []);

const SectionTitle = ({ children }) => <h6 className={styles.sectionTitle}>{children}</h6>;

const NumberField = ({ label, field, config, onChange, hint }) => (
  <DetailItem title={label}>
    <SimpleInput
      type="number"
      input_id={field}
      wrapperClassName={styles.noMargin}
      placeholder={hint}
      value={config[field] ?? ''}
      onInput={(v) => onChange(field, Number(v))}
    />
  </DetailItem>
);

const TextField = ({ label, field, config, onChange, hint, nullable = false }) => (
  <DetailItem title={label}>
    <SimpleInput
      input_id={field}
      wrapperClassName={styles.noMargin}
      placeholder={hint}
      value={config[field] ?? ''}
      onInput={(v) => onChange(field, nullable ? v || null : v)}
    />
  </DetailItem>
);

const TagsField = ({ label, field, config, onChange, hint, options = [] }) => (
  <DetailItem title={label}>
    <MultiSelect
      isMulti
      isCreatable
      placeholder={hint}
      options={options}
      value={toOptions(config[field] || [])}
      onChange={(opts) => onChange(field, fromOptions(opts))}
    />
  </DetailItem>
);

export const ConfigureAgent = ({ closeModal = () => {} }) => {
  const { fetchLeadConfigs, upsertLeadConfig } = useLeadConfigService();

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLeadConfigs().then(({ configs: [existing] }) => {
      if (!existing) return;
      setConfig({ ...DEFAULT_CONFIG, ...existing });
    });
  }, []);

  const set = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  const submit = async () => {
    setLoading(true);
    const payload = await upsertLeadConfig({ ...config });
    setLoading(false);
    if (!payload) return;
    toast.success('Config updated.');
    closeModal();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.form}>
        {/* ── General ─────────────────────────────────── */}
        <SectionTitle>General</SectionTitle>
        <DetailItem title="Slug">
          <SimpleInput
            input_id="slug"
            wrapperClassName={styles.noMargin}
            value={config.slug}
            onInput={(v) => set('slug', v)}
          />
        </DetailItem>

        {/* ── Apollo Org Search ───────────────────────── */}
        <SectionTitle>Apollo Org Search</SectionTitle>
        <div className={styles.grid3}>
          <NumberField label="Orgs per run" field="orgs_per_run" config={config} onChange={set} />
          <NumberField
            label="Min employees"
            field="org_employee_min"
            config={config}
            onChange={set}
          />
          <NumberField
            label="Max employees"
            field="org_employee_max"
            config={config}
            onChange={set}
          />
        </div>
        <div className={styles.grid2}>
          <TagsField
            label="Locations"
            field="org_locations"
            config={config}
            onChange={set}
            options={LOCATION_OPTIONS}
            hint="e.g. Nigeria, Kenya"
          />
          <TagsField
            label="Industries"
            field="org_industries"
            config={config}
            onChange={set}
            options={INDUSTRY_OPTIONS}
            hint="e.g. Fintech, SaaS"
          />
        </div>

        {/* ── Pipeline Limits ─────────────────────────── */}
        <SectionTitle>Pipeline Limits</SectionTitle>
        <div className={styles.grid2}>
          <NumberField label="Enrich limit" field="enrich_limit" config={config} onChange={set} />
          <NumberField
            label="Qualification threshold"
            field="qualification_threshold"
            config={config}
            onChange={set}
            hint="Min score to qualify"
          />
        </div>

        {/* ── Qualification Rubric ─────────────────────── */}
        <SectionTitle>Qualification Rubric (max 100)</SectionTitle>
        <div className={styles.grid3}>
          <NumberField
            label="Min social followers"
            field="social_followers_min"
            config={config}
            onChange={set}
          />
          <NumberField label="Min users" field="users_min" config={config} onChange={set} />
          <NumberField
            label="Score - Audience"
            field="score_audience"
            config={config}
            onChange={set}
            hint="≥ min followers or users"
          />
          <NumberField
            label="Score - Key contacts"
            field="score_key_contacts"
            config={config}
            onChange={set}
            hint="Public key contacts"
          />
          <NumberField
            label="Score - Has App"
            field="score_app"
            config={config}
            onChange={set}
            hint="Web or mobile app"
          />
          <NumberField
            label="Score - Has Newsletter"
            field="score_newsletter"
            config={config}
            onChange={set}
            hint="Signup form on site"
          />
        </div>

        {/* ── Warming ──────────────────────────────────── */}
        <SectionTitle>Warming</SectionTitle>
        <div className={styles.grid2}>
          <TextField
            label="Warming event code"
            field="warming_event_code"
            config={config}
            onChange={set}
            nullable
            hint="Go-Mailer automation event code"
          />
          <NumberField
            label="Warming days"
            field="warming_days"
            config={config}
            onChange={set}
            hint="Days before auto-move to ready_to_call"
          />
        </div>

        {/* ── Meetings ─────────────────────────────────── */}
        <SectionTitle>Meetings</SectionTitle>
        <div className={styles.grid3}>
          <TextField
            label="New meeting event"
            field="meeting_event_code"
            config={config}
            onChange={set}
            hint="Fired before the meeting"
          />
          <TextField
            label="Reschedule event"
            field="meeting_reschedule_event_code"
            config={config}
            onChange={set}
            hint="On time change"
          />
          <TextField
            label="Held event"
            field="meeting_held_event_code"
            config={config}
            onChange={set}
            hint="On status → held"
          />
          <TextField
            label="No-show event"
            field="meeting_no_show_event_code"
            config={config}
            onChange={set}
            hint="On status → no_show"
          />
          <TextField
            label="Cancel event"
            field="meeting_cancel_event_code"
            config={config}
            onChange={set}
            hint="On status → cancelled"
          />
        </div>
        <div className={styles.grid2}>
          <NumberField
            label="Reminder hours"
            field="meeting_reminder_hours"
            config={config}
            onChange={set}
            hint="Fire new_meeting this many hours before"
          />
          <NumberField
            label="Re-invite delay (minutes)"
            field="meeting_reinvite_delay_minutes"
            config={config}
            onChange={set}
            hint="If reschedule lands inside the window"
          />
        </div>

        {/* ── Contact Targeting ───────────────────────── */}
        <SectionTitle>Contact Targeting</SectionTitle>
        <div className={styles.grid2}>
          <TagsField
            label="Contact titles"
            field="contact_titles"
            config={config}
            onChange={set}
            options={TITLE_OPTIONS}
            hint="e.g. CEO, CTO, Founder"
          />
          <TagsField
            label="Contact departments"
            field="contact_departments"
            config={config}
            onChange={set}
            options={DEPARTMENT_OPTIONS}
            hint="e.g. marketing, sales"
          />
        </div>
        <div className={styles.grid2}>
          <TagsField
            label="Contact seniority"
            field="contact_seniority"
            config={config}
            onChange={set}
            options={SENIORITY_OPTIONS}
            hint="e.g. c_suite, director"
          />
          <NumberField
            label="Contacts per org"
            field="contacts_per_org"
            config={config}
            onChange={set}
          />
        </div>

        {/* ── Actions ──────────────────────────────────── */}
        <div className={styles.actions}>
          <Button text="Cancel" type="secondary" onClick={closeModal} loading={loading} />
          <Button icon_name="add" text="Save" onClick={submit} loading={loading} />
        </div>
      </div>
    </div>
  );
};
